import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  FormField,
  Input,
  Select,
  Multiselect,
  Autosuggest,
  ColumnLayout,
  Alert,
  DatePicker,
  ExpandableSection,
  Popover,
  Icon,
  RadioGroup,
} from "@cloudscape-design/components";
import {
  ProgramData,
  EditMode,
  CreationMethod,
  TargetSettingItem,
  BasicInfoFormData,
  WizardStep,
  DEFAULT_TARGET_SETTINGS,
  TARGET_CATEGORY_CONFIG,
  DEFAULT_CUSTOM_CATEGORY_CONFIG,
  extractProgramOptions,
  getLocationsForCm,
  mapProductFamiliesToOptions,
  DropdownOption,
  ApiProductFamily,
  ApiProgramOptions,
} from "../../types/configurationTypes";
import { RootState } from "../../redux/reducers/rootReducer";
import {
  createProductFamilyRequest,
  checkCategoryDuplicateRequest,
  getProductFamiliesRequest,
} from "../../redux/actions/configurationActions";

// ─── Props ──────────────────────────────────────────────────────────────────────
interface ProgramWizardProps {
  mode: EditMode;
  creationMethod: CreationMethod;
  selectedPrograms: ProgramData[];
  allPrograms: ProgramData[];
  onCancel: () => void;
  onSubmit: (
    mode: EditMode,
    data: { basic_info: Record<string, any>; target_settings: Array<{ category_subItem: string; target_value?: number; dri?: string }>; delete_target_settings?: string[] },
    bulkProgramIds?: string[]
  ) => void;
  /** Product families from API (for product family dropdown) */
  productFamilies: ApiProductFamily[];
  /** Cascading program options from Auto Report API */
  programOptions: ApiProgramOptions | null;
  /** Whether the submit API request is in progress */
  submitLoading?: boolean;
}

// ─── Helper: Convert string to Select option ────────────────────────────────────
const toOption = (value: string) =>
  value ? { label: value, value } : null;

// ─── Helper: Get mode title ─────────────────────────────────────────────────────
const getModeTitle = (mode: EditMode, _creationMethod: CreationMethod): string => {
  switch (mode) {
    case "create":
      return "Onboard New Program";
    case "editSingle":
      return "Update Program";
    case "editBulk":
      return "Batch Update Programs";
  }
};

// ─── Helper: Get mode description ───────────────────────────────────────────────
const getModeDescription = (mode: EditMode, _creationMethod: CreationMethod): string => {
  switch (mode) {
    case "create":
      return "Set up a new program. Fill in basic information in Step 1, then configure target settings in Step 2. You can optionally clone target settings from an existing program.";
    case "editSingle":
      return "Update the program configuration. Fields are pre-filled with existing values.";
    case "editBulk":
      return "Update multiple programs at once. Only the fields you modify will be applied to all selected programs.";
  }
};

// ─── Empty form data ────────────────────────────────────────────────────────────
const emptyBasicInfo: BasicInfoFormData = {
  program_name: "",
  cm: "",
  cm_location: "",
  operation: "",
  eom: "",
  ok2_ship: "",
  product_family_name: "",
  product_family_id: "",
  pvt_date: "",
  program_mapping: [],
  ur_only: undefined,
};

// ─── Helper: Get category config (built-in or custom) ───────────────────────────
const getCategoryConfig = (category: string) => {
  return TARGET_CATEGORY_CONFIG[category] || DEFAULT_CUSTOM_CATEGORY_CONFIG;
};

// ─── Helper: Get target placeholder per category ────────────────────────────────
const getTargetPlaceholder = (category: string): string => {
  switch (category) {
    case "Testing":
      return "0.95";
    case "SMT":
      return "0.95";
    case "Machine Downtime-SMT":
      return "0.02";
    case "Machine Downtime-FATP":
      return "0.02";
    case "Assembly":
    case "Output-SMT":
      return "0.99";
    case "Output-FATP":
      return "0.99";
    case "Material-SMT":
      return "0.95";
    case "Material-FATP":
      return "0.95";
    default:
      return "value range: 0 ~ 1";
  }
};

// ─── Component ──────────────────────────────────────────────────────────────────
const ProgramWizard: React.FC<ProgramWizardProps> = ({
  mode,
  creationMethod,
  selectedPrograms,
  allPrograms,
  onCancel,
  onSubmit,
  productFamilies,
  programOptions,
  submitLoading = false,
}) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // ─── Derive dropdown options from API data (flat lists) ─────────────────────
  const apiOptions = useMemo(
    () => extractProgramOptions(programOptions),
    [programOptions]
  );

  // Program name options from API
  const programNameOptions: DropdownOption[] = useMemo(
    () => apiOptions.programNames,
    [apiOptions.programNames]
  );
  const subProgramOptions: DropdownOption[] = useMemo(
    () => apiOptions.subPrograms,
    [apiOptions.subPrograms]
  );

  // ─── Determine if CM and Location should be disabled in bulk edit mode ──────
  // When bulk editing and 2+ selected programs share the same program_name, disable CM and Location
  const bulkDisableCmLocation = useMemo(() => {
    if (mode !== "editBulk") return false;
    const programNames = selectedPrograms.map((p) => p.program_name);
    const nameCount = new Map<string, number>();
    for (const name of programNames) {
      nameCount.set(name, (nameCount.get(name) || 0) + 1);
    }
    // If any program_name appears 2 or more times, disable CM and Location
    for (const count of nameCount.values()) {
      if (count >= 2) return true;
    }
    return false;
  }, [mode, selectedPrograms]);

  // ─── CM, Operation options from API ──────────────────────────────────────────
  const cmOptions: DropdownOption[] = useMemo(
    () => apiOptions.allCms,
    [apiOptions.allCms]
  );

  const operationOptions: DropdownOption[] = useMemo(
    () => apiOptions.allOperations,
    [apiOptions.allOperations]
  );
  // Product family options: use API data if available, otherwise fall back to static list
  const productFamilyOptions: DropdownOption[] = useMemo(
    () => {
      const apiOptions = mapProductFamiliesToOptions(productFamilies);
      return apiOptions
    },
    [productFamilies]
  );

  // Initialize form data based on mode
  const initialBasicInfo = useMemo((): BasicInfoFormData => {
    if (mode === "editSingle" && selectedPrograms.length === 1) {
      const p = selectedPrograms[0];
      return {
        program_name: p.program_name,
        cm: p.cm,
        cm_location: p.cm_location,
        operation: p.operation,
        eom: p.eom,
        ok2_ship: p.ok2_ship ? p.ok2_ship.split('T')[0] : '',
        product_family_name: p.product_family_name || "",
        product_family_id: p.product_family_id || "",
        pvt_date: p.pvt_date ? p.pvt_date.split('T')[0] : '',
        program_mapping: p.program_mapping || [],
        ur_only: p.ur_only || false,
      };
    }
    // For create, default ur_only to false; for bulk edit, keep undefined (no selection)
    if (mode === "create") {
      return { ...emptyBasicInfo, ur_only: false };
    }
    return { ...emptyBasicInfo };
  }, [mode, selectedPrograms]);

  const initialTargetSettings = useMemo((): TargetSettingItem[] => {
    if (mode === "editSingle" && selectedPrograms.length === 1) {
      const p = selectedPrograms[0];
      return p.target_settings && p.target_settings.length > 0
        ? p.target_settings.map((ts) => ({ ...ts }))
        : DEFAULT_TARGET_SETTINGS.map((ts) => ({ ...ts }));
    }
    return DEFAULT_TARGET_SETTINGS.map((ts) => ({ ...ts }));
  }, [mode, selectedPrograms]);

  const [basicInfo, setBasicInfo] = useState<BasicInfoFormData>(initialBasicInfo);
  const [targetSettings, setTargetSettings] = useState<TargetSettingItem[]>(initialTargetSettings);

  // Location options: filtered by selected CM (cascading CM → Location)
  const locationOptions: DropdownOption[] = useMemo(
    () => getLocationsForCm(programOptions, basicInfo.cm),
    [programOptions, basicInfo.cm]
  );

  // For clone mode: track selected clone source program
  const [cloneSourceId, setCloneSourceId] = useState<string>("");

  // For custom category creation
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  // For clone success alert auto-hide
  const [showCloneAlert, setShowCloneAlert] = useState(false);

  // For "no change" floating alert auto-hide (10 seconds)
  const [showNoChangeAlert, setShowNoChangeAlert] = useState(false);
  const noChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track pending product family creation to avoid duplicate dispatches
  const pendingProductFamilyRef = useRef<string | null>(null);

  // Redux state for product family creation and category check results
  const {
    createProductFamilyResult,
    createProductFamilyLoading,
    categoryCheckResult,
    categoryCheckLoading,
  } = useSelector((state: RootState) => state.configuration);

  // When a new product family is created successfully, update form and refresh list
  useEffect(() => {
    if (createProductFamilyResult && pendingProductFamilyRef.current) {
      const newId = createProductFamilyResult.product_family_id || pendingProductFamilyRef.current;
      setBasicInfo((prev) => ({
        ...prev,
        product_family_id: newId,
      }));
      pendingProductFamilyRef.current = null;
      // Refresh the product families list so the new one appears in the dropdown
      dispatch(getProductFamiliesRequest());
    }
  }, [createProductFamilyResult, dispatch]);

  // Auto-hide clone success alert after 10 seconds
  useEffect(() => {
    if (showCloneAlert) {
      const timer = setTimeout(() => {
        setShowCloneAlert(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [showCloneAlert]);

  // ─── Validation ─────────────────────────────────────────────────────────────
  const validateStep1 = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    // For bulk edit, no required fields (only modified fields are applied)
    if (mode === "editBulk") {
      setFormErrors({});
      return true;
    }

    if (!basicInfo.program_name.trim()) {
      errors.program_name = "Program name is required";
    }
    if (!basicInfo.cm) {
      errors.cm = "Contract manufacturer is required";
    }
    if (!basicInfo.cm_location) {
      errors.cm_location = "Location is required";
    }
    if (!basicInfo.operation.trim()) {
      errors.operation = "Operation is required";
    }
    if (!basicInfo.ok2_ship.trim()) {
      errors.ok2_ship = "OK2SHIP date is required";
    }
    // EOM is optional - no validation needed
    if (!basicInfo.product_family_name.trim()) {
      errors.product_family_name = "Product family is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [basicInfo, mode]);

  const validateStep2 = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    // For clone mode in create, must select a source program
    if (mode === "create" && creationMethod === "clone" && !cloneSourceId) {
      errors.cloneSource = "Please select a program to clone from";
    }

      // Validate all target values and DRI are filled (required for create and editSingle)
    let hasRequiredError = false;
    let hasRangeError = false;
    let hasDriFormatError = false;
    targetSettings.forEach((ts, idx) => {
      // Validation for create / editSingle: required + format
      if (mode === "create" || mode === "editSingle") {
        if (!ts.target_value && ts.target_value !== 0) {
          errors[`target_smt_${idx}`] = "Required";
          hasRequiredError = true;
        } else {
          const numVal = Number(ts.target_value);
          if (isNaN(numVal) || numVal < 0 || numVal > 1) {
            errors[`target_smt_${idx}`] = "Value must be between 0 and 1";
            hasRangeError = true;
          }
        }
        if (!ts.dri.trim()) {
          errors[`target_dri_${idx}`] = "Required";
          hasRequiredError = true;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ts.dri)) {
          errors[`target_dri_${idx}`] = "Invalid email format";
          hasDriFormatError = true;
        }
      }
      // Validation for editBulk: newly added categories are required, others only validate format if value exists
      else if (mode === "editBulk") {
        if (ts.isNewlyAdded) {
          // Newly added categories in bulk edit are required
          if (!ts.target_value && ts.target_value !== 0) {
            errors[`target_smt_${idx}`] = "Required";
            hasRequiredError = true;
          } else {
            const numVal = Number(ts.target_value);
            if (isNaN(numVal) || numVal < 0 || numVal > 1) {
              errors[`target_smt_${idx}`] = "Value must be between 0 and 1";
              hasRangeError = true;
            }
          }
          if (!ts.dri.trim()) {
            errors[`target_dri_${idx}`] = "Required";
            hasRequiredError = true;
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ts.dri)) {
            errors[`target_dri_${idx}`] = "Invalid email format";
            hasDriFormatError = true;
          }
        } else {
          // Non-newly-added categories: validate format only if value exists
          if (ts.target_value || ts.target_value == 0) {
            const numVal = Number(ts.target_value);
            if (isNaN(numVal) || numVal < 0 || numVal > 1) {
              errors[`target_smt_${idx}`] = "Value must be between 0 and 1";
              hasRangeError = true;
            }
          }
          if (ts.dri) {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ts.dri)) {
              errors[`target_dri_${idx}`] = "Invalid email format";
              hasDriFormatError = true;
            }
          }
        }
      }
    });
    // Build a combined error message for the single Alert
    const errorMessages: string[] = [];
    if (hasRequiredError) {
      errorMessages.push("All target values and DRI fields are required.");
    }
    if (hasRangeError) {
      errorMessages.push("Target values must be between 0 and 1.");
    }
    if (hasDriFormatError) {
      errorMessages.push("Some DRI fields have invalid email format.");
    }
    if (errorMessages.length > 0) {
      errors.targetGeneral = errorMessages.join(" ") + " Please fix the highlighted fields.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
}, [mode, creationMethod, cloneSourceId, targetSettings]);

  // ─── Navigation ─────────────────────────────────────────────────────────────
  const handleNext = useCallback(() => {
    if (validateStep1()) {
      setCurrentStep(2);
      setFormErrors({});
    }
  }, [validateStep1]);

  const handleBack = useCallback(() => {
    setCurrentStep(1);
    setFormErrors({});
  }, []);

  // ─── Collect changed data and transform to API-ready format ───────────────────
  const getChangedData = useCallback(() => {
    if (mode === "create") {
      return {
        basic_info:{
          ...basicInfo,
          product_family_name:basicInfo.product_family_name.trim(),
          ur_only: basicInfo.ur_only ?? false,
        } ,
        target_settings: targetSettings.map((ts) => ({
          category_subItem: ts.category_subItem,
          target_value: Number(ts.target_value),
          dri: ts.dri,
        })),
      };
    }

    // For edit modes, only include changed fields
    const basic_info: Record<string, any> = {};
    Object.entries(basicInfo).forEach(([key, val]) => {
      const initialVal = initialBasicInfo[key as keyof BasicInfoFormData];
      // Handle array fields (e.g., program_mapping) — compare by JSON serialization
      if (Array.isArray(val)) {
        const arraysEqual = JSON.stringify(val) === JSON.stringify(initialVal);
        if (!arraysEqual) {
          basic_info[key] = val;
        }
      } else if (typeof val === "boolean") {
        // Boolean fields (e.g., ur_only) — include if changed
        if (val !== initialVal) {
          basic_info[key] = val;
        }
      } else if (val !== initialVal) {
        basic_info[key] = (val as string).trim();
      }
    });

    const initialMap = new Map(
      initialTargetSettings.map((ts) => [ts.category_subItem, ts])
    );

    // Detect deleted custom categories: categories that existed in initialTargetSettings
    // but are no longer present in current targetSettings
    const currentCategorySet = new Set(targetSettings.map((ts) => ts.category_subItem));
    const delete_target_settings: string[] = initialTargetSettings
      .filter((ts) => {
        const isCustom = ts.isCustom || !TARGET_CATEGORY_CONFIG[ts.category_subItem];
        return isCustom && !currentCategorySet.has(ts.category_subItem);
      })
      .map((ts) => ts.category_subItem);

    const target_settings = targetSettings.map((ts): { category_subItem: string; target_value?: number; dri?: string } | null => {
        // Newly added categories are always considered a change (even with empty values)
        if (ts.isNewlyAdded) {
          const result: { category_subItem: string; target_value?: number; dri?: string } = {
            category_subItem: ts.category_subItem,
          };
          if (ts.target_value !== "" && ts.target_value !== null) {
            result.target_value = Number(ts.target_value);
          }
          if (ts.dri !== "") {
            result.dri = ts.dri;
          }
          return result;
        }

        const initial = initialMap.get(ts.category_subItem) || { target_value: "", dri: "" };
        const hasTarget = ts.target_value !== "" && ts.target_value !== null;
        const hasDri = ts.dri !== "";

        const targetChanged = hasTarget && ts.target_value !== initial.target_value;
        const driChanged = hasDri && ts.dri !== initial.dri;

        if (!targetChanged && !driChanged) return null;

        // Only collect the field(s) that actually changed and have a value
        const result: { category_subItem: string; target_value?: number; dri?: string } = {
          category_subItem: ts.category_subItem,
        };
        if (targetChanged && hasTarget) result.target_value = Number(ts.target_value);
        if (driChanged && hasDri) result.dri = ts.dri;

        if (result.target_value === undefined && result.dri === undefined) return null;
        return result;
      })
      .filter((item): item is { category_subItem: string; target_value?: number; dri?: string } => item !== null);

    return { basic_info, target_settings, delete_target_settings };
  }, [mode, basicInfo, targetSettings, initialBasicInfo, initialTargetSettings]);

  const handleSubmit = useCallback(() => {
    if (validateStep2()) {
      const submitData = getChangedData();

      // For edit modes, check if any content has actually been modified
      if (mode === "editSingle" || mode === "editBulk") {
        const hasBasicInfoChanges = Object.keys(submitData.basic_info).length > 0;
        const hasTargetSettingChanges = submitData.target_settings.length > 0;
        const hasDeleteTargetSettings = submitData.delete_target_settings && submitData.delete_target_settings.length > 0;

        if (!hasBasicInfoChanges && !hasTargetSettingChanges && !hasDeleteTargetSettings) {
          // Show floating "no change" alert with 10s auto-dismiss
          if (noChangeTimerRef.current) {
            clearTimeout(noChangeTimerRef.current);
          }
          setShowNoChangeAlert(true);
          noChangeTimerRef.current = setTimeout(() => {
            setShowNoChangeAlert(false);
            noChangeTimerRef.current = null;
          }, 10000);
          return;
        }
      }

      onSubmit(
        mode,
        submitData,
        mode === "editBulk"
          ? selectedPrograms.map((p) => p.program_id)
          : undefined
      );
    }
  }, [validateStep2, onSubmit, mode, selectedPrograms, getChangedData]);

  // ─── Clone handler ──────────────────────────────────────────────────────────
  const handleCloneFromProgram = useCallback(
    (programId: string) => {
      setCloneSourceId(programId);
      const sourceProgram = allPrograms.find((p) => p.program_id === programId);
      if (sourceProgram && sourceProgram.target_settings && sourceProgram.target_settings.length > 0) {
        // Clone target settings but preserve any existing custom categories
        setTargetSettings((prev) => {
          // Collect existing custom categories (user-added ones)
          const existingCustomCategories = prev.filter(
            (ts) => ts.isCustom || !TARGET_CATEGORY_CONFIG[ts.category_subItem]
          );

          // Clone the source program's target settings
          const clonedSettings = sourceProgram.target_settings.map((ts) => ({ ...ts }));

          // Merge: cloned settings + existing custom categories (avoid duplicates)
          const clonedCategoryNames = new Set(
            clonedSettings.map((ts) => ts.category_subItem.toLowerCase())
          );
          const customToKeep = existingCustomCategories.filter(
            (ts) => !clonedCategoryNames.has(ts.category_subItem.toLowerCase())
          );

          return [...clonedSettings, ...customToKeep];
        });
      }
      // Show clone success alert (auto-hides after 4s)
      setShowCloneAlert(true);

      // Clear clone source error
      if (formErrors.cloneSource) {
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next.cloneSource;
          return next;
        });
      }
    },
    [allPrograms, formErrors]
  );

  // ─── Form Update Helpers ────────────────────────────────────────────────────
  const updateBasicInfo = useCallback(
    (field: keyof BasicInfoFormData, value: string) => {
      setBasicInfo((prev) => ({ ...prev, [field]: value }));
      if (formErrors[field]) {
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [formErrors]
  );

  const updateTargetSetting = useCallback(
    (index: number, field: 'target_value' | 'dri', value: string) => {
      // For target_value, clamp the value between 0 and 1
      let finalValue = value;
      if (field === "target_value" && value !== "") {
        const numVal = Number(value);
        if (!isNaN(numVal)) {
          if (numVal > 1) finalValue = "1";
          else if (numVal < 0) finalValue = "0";
        }
      }

      setTargetSettings((prev) => {
        const newSettings = [...prev];
        newSettings[index] = { ...newSettings[index], [field]: finalValue };
        return newSettings;
      });
      // Clear related validation errors when user types
      const errorKey = field === "target_value" ? `target_smt_${index}` : field === "dri" ? `target_dri_${index}` : null;
      if (errorKey && formErrors[errorKey]) {
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next[errorKey];
          // Also clear the general error if no other target/dri errors remain
          const hasOtherErrors = Object.keys(next).some(
            (k) => k !== "targetGeneral" && (k.startsWith("target_smt_") || k.startsWith("target_dri_"))
          );
          if (!hasOtherErrors) {
            delete next.targetGeneral;
          }
          return next;
        });
      }
    },
    [formErrors]
  );

  // ─── Product Family: handle new creation via Autosuggest ────────────────────
  const productFamilyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether the user has confirmed their input (blur or select)
  const productFamilyConfirmedRef = useRef<boolean>(false);

const handleProductFamilyChange = useCallback(
    (value: string) => {
      // Clear any pending creation timer
      if (productFamilyTimerRef.current) {
        clearTimeout(productFamilyTimerRef.current);
        productFamilyTimerRef.current = null;
      }
      // User is still typing, so mark as unconfirmed
      productFamilyConfirmedRef.current = false;

      const trimmedValue = value.trim();
      const optionMatch = productFamilyOptions.find(
        (opt) => opt.value === value || opt.label.toLowerCase() === trimmedValue.toLowerCase()
      );

      if (optionMatch) {
        // Matched an existing option — display label (name), store value (ID)
        trimmedValue === value ? updateBasicInfo("product_family_name", optionMatch.label):updateBasicInfo("product_family_name", value);
        updateBasicInfo("product_family_id", optionMatch.value);
        pendingProductFamilyRef.current = null;
        productFamilyConfirmedRef.current = true;
      } else {
        // New value typed by user — use as both name and temporary ID
        updateBasicInfo("product_family_name", value);
        updateBasicInfo("product_family_id", value);

        // Do NOT trigger API check on every keystroke.
        // The API check will be triggered only when the user confirms input
        // (via blur or explicit selection). See handleProductFamilyBlur below.
      }
    },
    [productFamilyOptions, updateBasicInfo]
  );

  // Trigger the duplicate check only when the user has finished typing (on blur)
  const handleProductFamilyBlur = useCallback(() => {
    const trimmedValue = basicInfo.product_family_name.trim();
    if (!trimmedValue) return;

    // If user already selected an existing option, no need to check
    const optionMatch = productFamilyOptions.find(
      (opt) => opt.value === trimmedValue || opt.label.toLowerCase() === trimmedValue.toLowerCase()
    );
    if (optionMatch) return;

    // Mark as confirmed
    productFamilyConfirmedRef.current = true;

    // Debounce slightly to avoid double-fire, but short since user already left the field
    if (productFamilyTimerRef.current) {
      clearTimeout(productFamilyTimerRef.current);
    }
    productFamilyTimerRef.current = setTimeout(() => {
      if (!categoryCheckLoading && !createProductFamilyLoading) {
        const stillNotExists = !productFamilyOptions.find(
          (opt) => opt.label.toLowerCase() === trimmedValue.toLowerCase()
        );
        if (stillNotExists && pendingProductFamilyRef.current !== trimmedValue) {
          pendingProductFamilyRef.current = trimmedValue;
          dispatch(checkCategoryDuplicateRequest(trimmedValue));
        }
      }
    }, 300);
  }, [basicInfo.product_family_name, productFamilyOptions, categoryCheckLoading, createProductFamilyLoading, dispatch]);

  // Step 2: When check result comes back, create product family if it doesn't exist
  useEffect(() => {
    if (categoryCheckResult && pendingProductFamilyRef.current) {
      if (categoryCheckResult.exists === false) {
        // Product family doesn't exist — create it
        const nameToCreate = pendingProductFamilyRef.current;
        dispatch(
          createProductFamilyRequest({
            product_family_name: nameToCreate,
          })
        );
      } else {
        // Product family already exists — clear pending ref
        pendingProductFamilyRef.current = null;
      }
    }
  }, [categoryCheckResult, dispatch]);

  // Cleanup product family timer and noChange timer on unmount
  useEffect(() => {
    return () => {
      if (productFamilyTimerRef.current) {
        clearTimeout(productFamilyTimerRef.current);
      }
      if (noChangeTimerRef.current) {
        clearTimeout(noChangeTimerRef.current);
      }
    };
  }, []);

  // ─── Custom Category Handlers ───────────────────────────────────────────────
  const handleAddCustomCategory = useCallback(() => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) return;

    // Check for duplicate category names locally
    const exists = targetSettings.some(
      (ts) => ts.category_subItem.toLowerCase() === trimmedName.toLowerCase()
    );
    if (exists) {
      setFormErrors((prev) => ({
        ...prev,
        newCategory: "A category with this name already exists",
      }));
      return;
    }

    // If editing a single program, also check via API
    // if (mode === "editSingle" && selectedPrograms.length === 1) {
    //   dispatch(
    //     checkCategoryDuplicateRequest(trimmedName)
    //   );
    // }

    const newItem: TargetSettingItem = {
      category_subItem: trimmedName,
      target_value: '',
      dri: "",
      isCustom: true,
      isNewlyAdded: true,
    };

    setTargetSettings((prev) => [...prev, newItem]);

    setNewCategoryName("");
    setShowAddCategory(false);
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.newCategory;
      return next;
    });
  }, [newCategoryName, targetSettings, mode, selectedPrograms, dispatch]);

  const handleRemoveCustomCategory = useCallback(
    (index: number) => {
      setTargetSettings((prev) => prev.filter((_, i) => i !== index));
    },
    []
  );

  // ─── Sorted target settings: custom categories at the end ───────────────────
  const sortedTargetSettings = useMemo(() => {
    const nonCustom: { item: TargetSettingItem; originalIndex: number }[] = [];
    const custom: { item: TargetSettingItem; originalIndex: number }[] = [];
    targetSettings.forEach((ts, idx) => {
      const isCustom = ts.isCustom || !TARGET_CATEGORY_CONFIG[ts.category_subItem];
      if (isCustom) {
        custom.push({ item: ts, originalIndex: idx });
      } else {
        nonCustom.push({ item: ts, originalIndex: idx });
      }
    });
    // Sort non-custom items according to DEFAULT_TARGET_SETTINGS order
    const orderMap = new Map(DEFAULT_TARGET_SETTINGS.map((ts, i) => [ts.category_subItem, i]));
    nonCustom.sort((a, b) => {
      const orderA = orderMap.get(a.item.category_subItem) ?? 999;
      const orderB = orderMap.get(b.item.category_subItem) ?? 999;
      return orderA - orderB;
    });
    return [...nonCustom, ...custom];
  }, [targetSettings]);

  // ─── Memoized sub-program selected options (avoid re-render from new array ref) ──
  const subProgramSelectedOptions = useMemo(
    () => basicInfo.program_mapping.map((name) => ({ label: name, value: name })),
    [basicInfo.program_mapping]
  );

  // ─── Track whether the Multiselect dropdown is expanded ─────────────────────
  const [isSubProgramDropdownOpen, setIsSubProgramDropdownOpen] = useState(false);
  const subProgramContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const containerEl = subProgramContainerRef.current;
    if (!containerEl) return;

    const observer = new MutationObserver(() => {
      const toggleEl = containerEl.querySelector('[class*="awsui_toggle"]') as HTMLElement | null;
      if (toggleEl) {
        const expanded = toggleEl.getAttribute("aria-expanded") === "true";
        setIsSubProgramDropdownOpen(expanded);
      }
    });

    observer.observe(containerEl, {
      attributes: true,
      attributeFilter: ["aria-expanded"],
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // ─── Render: Step Indicator ─────────────────────────────────────────────────
  const renderStepIndicator = () => (
    <div className="wizard-steps-indicator">
      <div className="wizard-step-item">
        <div
          className={`wizard-step-number ${
            currentStep === 1 ? "active" : "completed"
          }`}
        >
          {currentStep > 1 ? "✓" : "1"}
        </div>
        <span
          className={`wizard-step-label ${
            currentStep === 1 ? "active" : "completed"
          }`}
        >
          Basic Information
        </span>
      </div>
      <div
        className={`wizard-step-connector ${
          currentStep > 1 ? "completed" : ""
        }`}
      />
      <div className="wizard-step-item">
        <div
          className={`wizard-step-number ${
            currentStep === 2 ? "active" : ""
          }`}
        >
          2
        </div>
        <span
          className={`wizard-step-label ${
            currentStep === 2 ? "active" : ""
          }`}
        >
          Target Settings
        </span>
      </div>
    </div>
  );

  // ─── Render: Step 1 - Basic Info ────────────────────────────────────────────
  const renderStep1 = () => (
    <Container
      header={
        <Header
          variant="h2"
          description={
            mode === "editBulk"
              ? "Leave fields empty to keep existing values. Only modified fields will be applied."
              : ""
          }
        >
          Step 1: Basic Information
        </Header>
      }
    >
    <SpaceBetween size="l">
        {mode === "editBulk" ? (
          <Alert key="bulk-edit-alert" type="info">
            <strong>Batch Update Mode:</strong> You are editing{" "}
            <strong>{selectedPrograms.length}</strong> programs. Only the fields
            you fill in will be updated. Empty fields will retain their current
            values.
            <div className="selected-programs-list" style={{ marginTop: 8 }}>
              {selectedPrograms.map((p) => (
                <span key={p.program_id} className="selected-program-name">
                  {p.program_name}
                </span>
              ))}
            </div>
          </Alert>
        ) : null}
        {/* : mode === "editSingle" && selectedPrograms.length === 1 ? (
          <Alert key="single-edit-alert" type="info">
            <strong>Update Mode:</strong> You are editing program{" "}
            <strong>{selectedPrograms[0].program_name}</strong>. Fields are
            pre-filled with existing values. Modify the fields you want to
            update.
          </Alert>
        )  */}

        {mode === "create" && creationMethod === "clone" ? (
          <Alert key="clone-mode-alert" type="info">
            <strong>Clone Mode:</strong> Fill in the basic information for the new program.
            In Step 2, you will select an existing program to clone target settings from.
            Only target settings data will be cloned.
          </Alert>
        ) : null}

        <ColumnLayout columns={2}>
          {/* Program Name - Select from Auto Report cascading options */}
          <FormField
            label={
              <span>
                Program <span style={{ color: "#d13212" }}>*</span>
              </span>
            }
            errorText={formErrors.program_name}
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : ""
            }
          >
            <Select
              data-testid="onboard-select-program"
              selectedOption={toOption(basicInfo.program_name)}
              onChange={({ detail }) =>
                updateBasicInfo(
                  "program_name",
                  detail.selectedOption.value || ""
                )
              }
              options={programNameOptions}
              placeholder="Select program"
              filteringType="auto"
              disabled={mode === "editBulk"}
              empty="No programs available"
            />
          </FormField>

          {/* Sub-Programs - Multiselect for program name mapping */}
          <FormField
            label={
              <span>
                Sub-Programs{" "}
                <span className="form-field-optional">(Optional)</span>{" "}
                <Popover
                  dismissButton={false}
                  position="top"
                  size="medium"
                  triggerType="custom"
                  content={
                    mode === "editBulk"
                      ? "Leave empty to keep existing values. Select program names that are variants of this program."
                      : "Select program names that are variants of this program across different stages."
                  }
                >
                  <span style={{ cursor: "pointer", color: "#5f6b7a"}}>
                    <Icon name="status-info" size="small" />
                  </span>
                </Popover>
              </span>
            }
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : ""
            }
          >
            <div ref={subProgramContainerRef} className={`sub-programs-multiselect-container${isSubProgramDropdownOpen ? '-expanded' : ''}`}>
              <Multiselect
                data-testid="onboard-select-sub-programs"
                selectedOptions={subProgramSelectedOptions}
                onChange={({ detail }) => {
                  const selectedValues = detail.selectedOptions.map(
                    (opt) => opt.value || ""
                  ).filter(Boolean);
                  setBasicInfo((prev) => ({ ...prev, program_mapping: selectedValues }));
                }}
                options={subProgramOptions}
                placeholder="Select sub-programs"
                filteringType="auto"
                tokenLimit={3}
                disabled={mode === "editBulk"}
                empty="No programs available"
              />
            </div>
          </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          {/* CM - Cascading: filtered by selected program */}
            <FormField
              label={
                <span>
                  CM <span style={{ color: "#d13212" }}>*</span>
                </span>
              }
              errorText={formErrors.cm}
              description={
                mode === "editBulk"
                  ? "Leave empty to keep existing values"
                  : ""
              }
            >
              <Select
                data-testid="onboard-select-cm"
                selectedOption={toOption(basicInfo.cm)}
                onChange={({ detail }) => {
                  const newCm = detail.selectedOption.value || "";
                  updateBasicInfo("cm", newCm);
                // When CM changes, clear location only if it's not in the new filtered location list
                if (newCm !== basicInfo.cm && basicInfo.cm_location) {
                  const newLocationOptions = getLocationsForCm(programOptions, newCm);
                  const locationStillExists = newLocationOptions.some(
                    (opt) => opt.value === basicInfo.cm_location
                  );
                  if (!locationStillExists) {
                    setBasicInfo((prev) => ({ ...prev, cm_location: "" }));
                  }
                }
                }}
                options={cmOptions}
                placeholder="Select CM"
                disabled={bulkDisableCmLocation}
                empty="No CM options available"
              />
            </FormField>

            {/* Location/Site - Cascading: filtered by selected program + CM */}
            <FormField
              label={
                <span>
                  Location <span style={{ color: "#d13212" }}>*</span>
                </span>
              }
              errorText={formErrors.cm_location}
              description={
                mode === "editBulk"
                  ? "Leave empty to keep existing values"
                  : ""
              }
            >
              <Select
                data-testid="onboard-select-location"
                selectedOption={toOption(basicInfo.cm_location)}
                onChange={({ detail }) =>
                  updateBasicInfo("cm_location", detail.selectedOption.value || "")
                }
                options={locationOptions}
                placeholder="Select location"
                disabled={bulkDisableCmLocation}
                empty="No locations available"
              />
            </FormField>
        </ColumnLayout>
        <ColumnLayout columns={2}>
          {/* Operation - from API or fallback to hardcoded list */}
          <FormField
            label={
              <span>
                Operation <span style={{ color: "#d13212" }}>*</span>
              </span>
            }
            errorText={formErrors.operation}
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : ""
            }
          >
            <Select
              data-testid="onboard-select-operation"
              selectedOption={toOption(basicInfo.operation)}
              onChange={({ detail }) =>
                updateBasicInfo("operation", detail.selectedOption.value || "")
              }
              options={operationOptions}
              placeholder="Select operation"
              empty="No operations available"
            />
          </FormField>
          {/* Product Family - Select from API or type a new value */}
          <FormField
            label={
              <span>
                Product Family <span style={{ color: "#d13212" }}>*</span>
              </span>
            }
            errorText={formErrors.product_family_name}
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : ""
            }
          >
            <div onBlur={handleProductFamilyBlur}>
              <Autosuggest
                data-testid="onboard-input-product-family"
                value={basicInfo.product_family_name}
                onChange={({ detail }) => 
                  handleProductFamilyChange(detail.value)
                }
                options={productFamilyOptions}
                placeholder="Select or type product family"
                enteredTextLabel={(value) => `Use: "${value}"`}
                empty="No matching product family — type to create new"
              />
            </div>
          </FormField>
        </ColumnLayout>

        <ColumnLayout columns={2}>
          {/* OK2SHIP - Date Picker (stored as ISO 8601 timestamp) */}
          <FormField
            label={
              <span>
                OK2SHIP <span style={{ color: "#d13212" }}>*</span>
              </span>
            }
            errorText={formErrors.ok2_ship}
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : ""
            }
          >
            <DatePicker
              data-testid="onboard-date-ok2ship"
              value={basicInfo.ok2_ship ? basicInfo.ok2_ship.split('T')[0] : ""}
              onChange={({ detail }) =>
                updateBasicInfo("ok2_ship", detail.value )
              }
              placeholder="YYYY-MM-DD"
              openCalendarAriaLabel={(selectedDate) =>
                `Choose OK2SHIP date${selectedDate ? `, selected date is ${selectedDate}` : ""}`
              }
            />
          </FormField>

          {/* PVT Date - Date Picker */}
          <FormField
            label={
              <span>
                PVT Date{" "}
                <span className="form-field-optional">(Optional)</span>
              </span>
            }
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : undefined
            }
          >
            <DatePicker
              data-testid="onboard-date-pvt"
              value={basicInfo.pvt_date}
              onChange={({ detail }) =>
                updateBasicInfo("pvt_date", detail.value)
              }
              placeholder="YYYY-MM-DD"
              openCalendarAriaLabel={(selectedDate) =>
                `Choose PVT date${selectedDate ? `, selected date is ${selectedDate}` : ""}`
              }
            />
          </FormField>
        </ColumnLayout>

        <ColumnLayout columns={2}>
          {/* EOM - Optional, Date Picker */}
          <FormField
            label={
              <span>
                EOM (End of Manufacturing){" "}
                <span className="form-field-optional">(Optional)</span>
              </span>
            }
            description={
              mode === "editBulk"
                ? "Leave empty to keep existing values"
                : ""
            }
          >
            <DatePicker
              data-testid="onboard-date-eom"
              value={basicInfo.eom}
              onChange={({ detail }) => updateBasicInfo("eom", detail.value)}
              placeholder="YYYY-MM-DD"
              openCalendarAriaLabel={(selectedDate) =>
                `Choose EOM date${selectedDate ? `, selected date is ${selectedDate}` : ""}`
              }
            />
          </FormField>

          {/* Factory Factor only - RadioGroup with Yes/No options */}
          <FormField
            label={
              <span>
                Factory Factor only{" "}
                <span className="form-field-optional">(Optional)</span>{" "}
                <Popover
                  dismissButton={false}
                  position="top"
                  size="medium"
                  triggerType="custom"
                  content="This option applies only to the Utilization Ratio (UR) feature. Enable it if this program should be included in UR calculations."
                >
                  <span style={{ cursor: "pointer",color:'#5f6b7a'}}>
                    <Icon name="status-info" size="small" />
                  </span>
                </Popover>
              </span>
            }
            description={
              mode === "editBulk"
                ? "Leave unselected to keep existing values"
                : ""
            }
          >
            <RadioGroup
              data-testid="onboard-toggle-ur-only"
              direction="horizontal"
              value={basicInfo.ur_only === true ? "true" : basicInfo.ur_only === false ? "false" : null}
              onChange={({ detail }) =>
                setBasicInfo((prev) => ({ ...prev, ur_only: detail.value === "true" }))
              }
              items={[
                { value: "true", label: "Yes" },
                { value: "false", label: "No" },
              ]}
            />
          </FormField>
        </ColumnLayout>

      </SpaceBetween>
    </Container>
  );

  // ─── Render: Clone Source Selector (Collapsible) ────────────────────────────
  const renderCloneSourceSelector = () => {
    const isCloneMode = mode === "create" && creationMethod === "clone";

    const programSelectOptions = allPrograms.map((p) => ({
      value: p.program_id,
      label: `${p.program_name} — ${p.cm} / ${p.cm_location}`,
    }));

    return (
      <ExpandableSection
        headerText={
          isCloneMode
            ? "Clone from Existing Program"
            : "Clone from Existing Program (Optional)"
        }
        defaultExpanded={isCloneMode || !!cloneSourceId}
        variant="container"
      >
        <SpaceBetween size="s">
          {/* Select + Clear Clone button on the same line */}
          <FormField
            label={
              isCloneMode ? (
                <span>
                  Select Program to Clone <span style={{ color: "#d13212" }}>*</span>
                </span>
              ) : (
                "Select a program to clone target settings from"
              )
            }
            errorText={formErrors.cloneSource}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <Select
                  data-testid="onboard-select-clone"
                  selectedOption={
                    cloneSourceId
                      ? programSelectOptions.find((o) => o.value === cloneSourceId) || null
                      : null
                  }
                  onChange={({ detail }) =>
                    handleCloneFromProgram(detail.selectedOption.value || "")
                  }
                  options={programSelectOptions}
                  placeholder="Select a program to clone from"
                  filteringType="auto"
                />
              </div>
              {cloneSourceId && (
                <Button
                  data-testid="onboard-btn-clear-clone"
                  variant="normal"
                  onClick={() => {
                    setCloneSourceId("");
                    setShowCloneAlert(false);
                    setTargetSettings((prev) => {
                      // Restore to original data (initialTargetSettings)
                      const restored = initialTargetSettings.map((ts) => ({ ...ts }));

                      // Preserve user-added custom categories (isNewlyAdded) that don't conflict with initialTargetSettings
                      const restoredCategoryNames = new Set(
                        restored.map((ts) => ts.category_subItem.toLowerCase())
                      );
                      const userAddedCustom = prev.filter(
                        (ts) => ts.isNewlyAdded && !restoredCategoryNames.has(ts.category_subItem.toLowerCase())
                      );

                      return [...restored, ...userAddedCustom];
                    });
                  }}
                >
                  Restore Default
                </Button>
              )}
            </div>
          </FormField>

          {/* Success alert: auto-hides after 4 seconds with fade-out */}
          {cloneSourceId && showCloneAlert ? (
            <div key="clone-success-alert" className="clone-alert-fade">
              <Alert type="success">
                Cloned from{" "}
                <strong>
                  {allPrograms.find((p) => p.program_id === cloneSourceId)?.program_name}
                </strong>
                . You can modify the values below.
              </Alert>
            </div>
          ) : null}
        </SpaceBetween>
      </ExpandableSection>
    );
  };

  // ─── Render: Step 2 - Target Settings ───────────────────────────────────────
  const renderStep2 = () => (
    <SpaceBetween size="l">
      {mode === "editBulk" ? (
        <Alert key="bulk-edit-step2-alert" type="info">
          <strong>Batch Update Mode:</strong> Only the fields you modify will be
          applied to all {selectedPrograms.length} selected programs. Leave
          fields empty to keep existing values.
          <div className="selected-programs-list" style={{ marginTop: 8 }}>
              {selectedPrograms.map((p) => (
                <span key={p.program_id} className="selected-program-name">
                  {p.program_name}
                </span>
              ))}
            </div>
        </Alert>
      ) : null}
      {/* : mode === "editSingle" && selectedPrograms.length === 1 ? (
        <Alert key="single-edit-step2-alert" type="info">
          <strong>Update Mode:</strong> You are editing program{" "}
          <strong>{selectedPrograms[0].program_name}</strong>. Fields are
          pre-filled with existing values. Modify the fields you want to
          update.
        </Alert>
      )  */}

      {/* Clone Source Selector */}
      {renderCloneSourceSelector()}

      {/* Target Settings - Compact Table Layout */}
      <Container
        header={
          <Header
            variant="h2"
            description="Set DRI and target values for each category. Assembly applies to FATP only. You can add custom categories below."
          >
            Step 2: Target Settings
          </Header>
        }
      >
        <SpaceBetween size="m">
          {formErrors.targetGeneral ? (
            <Alert key="target-general-error" type="error">
              {formErrors.targetGeneral}
            </Alert>
          ) : null}
          {showNoChangeAlert ? (
            <Alert
              key="no-change-alert"
              type="warning"
              dismissible
              onDismiss={() => {
                setShowNoChangeAlert(false);
                if (noChangeTimerRef.current) {
                  clearTimeout(noChangeTimerRef.current);
                  noChangeTimerRef.current = null;
                }
              }}
            >
              No content has been changed. Please modify at least one field before submitting.
            </Alert>
          ) : null}
          
          <div className="target-table" data-testid="onboard-target-table">
            {/* Table Header */}
            <div className="target-table-header">
              <div className="target-table-cell target-table-category">Category</div>
              <div className="target-table-cell target-table-col">Target</div>
              <div className="target-table-cell target-table-col">DRI</div>
              <div className="target-table-cell target-table-action"></div>
            </div>

            {/* Table Body */}
            {sortedTargetSettings.map(({ item: ts, originalIndex }) => {
              const config = getCategoryConfig(ts.category_subItem);
              const isCustom = ts.isCustom || !TARGET_CATEGORY_CONFIG[ts.category_subItem];
              const rowTestId = isCustom
                ? `onboard-target-row-custom-${ts.category_subItem}`
                : `onboard-target-row-${ts.category_subItem.toLowerCase().replace(/\s+/g, "-")}`;
              return (
                <div key={`${ts.category_subItem}-${originalIndex}`} className="target-table-row" data-testid={rowTestId}>
                  <div className="target-table-cell target-table-category">
                    <span className="category-name">{ts.category_subItem}</span>
                    {(mode === "create" || mode === "editSingle" || (mode === "editBulk" && ts.isNewlyAdded)) && (
                      <span style={{ color: "#d13212", marginLeft: 2 }}>*</span>
                    )}
                    {isCustom && (
                      ts.isNewlyAdded ? (
                        <span className="custom-category-tag newly-added">✦ New Custom</span>
                      ) : (
                        <span className="custom-category-tag">Custom</span>
                      )
                    )}
                  </div>

                  {/* Target */}
                  <div className="target-table-cell target-table-col">
                    <Input
                      value={String(ts.target_value)}
                      onChange={({ detail }) =>
                        updateTargetSetting(originalIndex, "target_value", detail.value)
                      }
                      placeholder={getTargetPlaceholder(ts.category_subItem)}
                      type="number"
                      inputMode="decimal"
                      step={0.01}
                      invalid={!!formErrors[`target_smt_${originalIndex}`]}
                    />
                  </div>

                  {/* DRI */}
                  <div className="target-table-cell target-table-col">
                    <Input
                      value={ts.dri}
                      onChange={({ detail }) =>
                      updateTargetSetting(originalIndex, "dri", detail.value.replace(/\s+/g, ''))
                      }
                      placeholder="name@email.com"
                      invalid={!!formErrors[`target_dri_${originalIndex}`] || (!!ts.dri && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ts.dri))}
                    />
                  </div>

                  {/* Remove button for custom categories */}
                  <div className="target-table-cell target-table-action">
                    {isCustom && (
                      <Button
                        data-testid={`onboard-target-row-custom-${ts.category_subItem}-delete`}
                        variant="icon"
                        iconName="close"
                        onClick={() => handleRemoveCustomCategory(originalIndex)}
                        ariaLabel={`Remove ${ts.category_subItem}`}
                      />
                    )}
                  </div>
                </div>
              );
            })
          }
          </div>

          {/* Add Custom Category Form / Button */}
          {showAddCategory ? (
            <div key="add-category-form" className="add-category-form">
              <SpaceBetween direction="horizontal" size="xs">
                <FormField
                  errorText={formErrors.newCategory}
                >
                  <Input
                    data-testid="onboard-custom-category-input"
                    value={newCategoryName}
                    onChange={({ detail }) => {
                      setNewCategoryName(detail.value);
                      if (formErrors.newCategory) {
                        setFormErrors((prev) => {
                          const next = { ...prev };
                          delete next.newCategory;
                          return next;
                        });
                      }
                    }}
                    placeholder="Enter custom category name"
                  />
                </FormField>
                <Button
                  data-testid="onboard-custom-category-confirm"
                  variant="primary"
                  onClick={handleAddCustomCategory}
                  disabled={!newCategoryName.trim()}
                >
                  Add
                </Button>
                <Button
                  data-testid="onboard-custom-category-cancel"
                  variant="link"
                  onClick={() => {
                    setShowAddCategory(false);
                    setNewCategoryName("");
                    setFormErrors((prev) => {
                      const next = { ...prev };
                      delete next.newCategory;
                      return next;
                    });
                  }}
                >
                  Cancel
                </Button>
              </SpaceBetween>
            </div>
          ) : (
            <Button
              data-testid="onboard-btn-add-category"
              key="add-category-button"
              variant="normal"
              iconName="add-plus"
              onClick={() => setShowAddCategory(true)}
            >
              Add Custom Category
            </Button>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );

  // ─── Render: Main ───────────────────────────────────────────────────────────
  return (
    <div className="wizard-container" data-testid="onboard-wizard">
      <SpaceBetween size="m">
        {/* Header */}
        <Header variant="h2">
          {getModeTitle(mode, creationMethod)}
        </Header>

        {/* Step Indicator (own line, compact) */}
        {renderStepIndicator()}

        {/* Step Content */}
        {currentStep === 1 ? renderStep1() : renderStep2()}

        {/* Navigation Buttons */}
        <div className="wizard-navigation">
          <Button variant="link" onClick={onCancel} data-testid="onboard-btn-cancel">
            Cancel
          </Button>
          {currentStep === 2 && (
            <Button variant="normal" onClick={handleBack} data-testid="onboard-btn-back">
              ← Back
            </Button>
          )}
          {currentStep === 1 ? (
            <Button variant="primary" onClick={handleNext} data-testid="onboard-btn-next">
              Next →
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitLoading}
              disabled={submitLoading}
              data-testid="onboard-btn-create">
              {mode === "create"
                ? creationMethod === "clone" && cloneSourceId
                  ? "Clone & Create Program"
                  : "Create Program"
                : mode === "editSingle"
                  ? "Update Program"
                  : `Update ${selectedPrograms.length} Programs`}
            </Button>
          )}
        </div>
      </SpaceBetween>
    </div>
  );
};

export default ProgramWizard;
